// src/components/tutor/ToolCard.tsx
//
// Renders a tool call or result inline in the chat. Three visual modes
// based on tier:
//   - auto         → "✓ <tool> ran" — subtle, completed look
//   - soft_confirm → inline button with optional preview blurb
//   - hard_confirm → preview card with diff + Apply/Cancel
//
// Resolution lifecycle:
//   pending          → render confirm UI (soft or hard)
//   resolved.ok      → render done state (green check)
//   resolved.error   → render error state (red ring)
//   resolved.cancel  → render dimmed "cancelled" state
//
// A result that carries a launchable activity — offer_activity's server-built
// `action`, or a drill an older generate_drill call built — renders as a Start
// card (SageActionCard) instead of a pill. It's drawn from the STORED result,
// with no confirm step and no expiry, so it stays clickable after the thread
// is reloaded from history.

'use client'

import { planCheckSummary } from '@/lib/tutor-tools/plan-check-summary'
import { planCheckFits } from '@/lib/tutor-tools/plan-check-layout'
import { useState } from 'react'
import { CheckCircle2, AlertCircle, ArrowRight, Loader2 } from 'lucide-react'
import type { SageAction } from '@unlimitedtests/core'
import { Button } from '@/components/ui/button'
import { sageActionFromToolResult } from '@/lib/tutor-tools/sage-action-builders'
import SageActionCard from './SageActionCard'
import { METRIC_LABELS } from '@/lib/progress/metric-labels'

export type ToolCardCall = {
  id: string
  name: string
  tier: 'auto' | 'soft_confirm' | 'hard_confirm'
  args: unknown
  preview?: unknown
}

export type ToolCardResult = {
  id: string
  ok: boolean
  result?: unknown
  error?: string
  ranAt: string
}

export default function ToolCard({
  call,
  result,
  messageId,
  onResolved,
  onLaunch,
}: {
  call: ToolCardCall
  result?: ToolCardResult
  messageId: string
  onResolved?: () => void
  /** Called when the student opens an activity from a Start card (see SageActionCard). */
  onLaunch?: (action: SageAction) => void
}) {
  const [busy, setBusy] = useState(false)
  const [localResult, setLocalResult] = useState<ToolCardResult | undefined>(result)
  const effective = localResult ?? result

  // Already resolved (came from server with a result)
  if (effective) {
    return <ResolvedView call={call} result={effective} onLaunch={onLaunch} />
  }

  // Auto tool with no result yet → loading
  if (call.tier === 'auto') {
    return (
      <Pill icon={<Loader2 className="h-4 w-4 animate-spin" />} text={runningLabel(call.name)} />
    )
  }

  // Pending soft/hard confirm
  const apply = async () => {
    setBusy(true)
    try {
      const res = await fetch('/api/tutor/tool/execute', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        cache: 'no-store',
        body: JSON.stringify({ messageId, toolCallId: call.id }),
      })
      const data = await res.json()
      if (!res.ok || data.error) {
        setLocalResult({
          id: call.id,
          ok: false,
          error: data?.error ?? 'Failed',
          ranAt: new Date().toISOString(),
        })
      } else {
        setLocalResult({
          id: call.id,
          ok: true,
          result: data.result,
          ranAt: new Date().toISOString(),
        })
        // Special case: start_drill should navigate
        if (call.name === 'start_drill' && data.result?.startUrl) {
          window.location.assign(data.result.startUrl)
        }
      }
      onResolved?.()
    } finally {
      setBusy(false)
    }
  }

  const cancel = async () => {
    setBusy(true)
    try {
      const res = await fetch('/api/tutor/tool/cancel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        cache: 'no-store',
        body: JSON.stringify({ messageId, toolCallId: call.id }),
      })
      if (res.ok) {
        setLocalResult({
          id: call.id,
          ok: false,
          error: 'cancelled by user',
          ranAt: new Date().toISOString(),
        })
        onResolved?.()
      }
    } finally {
      setBusy(false)
    }
  }

  if (call.tier === 'soft_confirm') {
    return <SoftConfirmView call={call} busy={busy} onApply={apply} onCancel={cancel} />
  }
  return <HardConfirmView call={call} busy={busy} onApply={apply} onCancel={cancel} />
}

/* ------------------- Sub-views ------------------- */

function ResolvedView({
  call,
  result,
  onLaunch,
}: {
  call: ToolCardCall
  result: ToolCardResult
  onLaunch?: (action: SageAction) => void
}) {
  if (call.name === 'offer_activity') {
    // The action is re-checked here, not trusted: a row stored under older
    // rules, or one that fails the allow-list, gets no button rather than a
    // wrong one. A failed offer's error was written for the model ("Unknown
    // topic …") and Sage's reply already says what went wrong, so the
    // student sees a quiet note, not the raw error.
    const action = result.ok ? sageActionFromToolResult(call.name, result.result) : null
    if (action) return <SageActionCard action={action} onLaunch={onLaunch} />
    return (
      <Pill
        icon={<AlertCircle className="h-3.5 w-3.5" />}
        text="Couldn't set that up"
        tone="muted"
      />
    )
  }
  if (call.name === 'generate_drill' && result.ok) {
    // A drill built before offer_activity existed: rebuilt from its stored
    // question ids so replayed history gains the Start button it never had.
    // An empty one (the old tool's startUrl '') falls through to the pill.
    const action = sageActionFromToolResult(call.name, result.result)
    if (action) return <SageActionCard action={action} onLaunch={onLaunch} />
  }
  if (!result.ok) {
    const cancelled = result.error?.toLowerCase().includes('cancel')
    return (
      <Pill
        icon={<AlertCircle className="h-3.5 w-3.5" />}
        text={
          cancelled
            ? `${humanize(call.name)} — cancelled`
            : `${humanize(call.name)} — failed${result.error ? ': ' + result.error : ''}`
        }
        tone={cancelled ? 'muted' : 'error'}
      />
    )
  }
  // Success — bespoke summaries for a few key tools, generic for the rest
  const summary = summarizeResult(call, result)
  if (call.name === 'check_plan_time' && !planCheckFits(result.result)) {
    // The check ran, but the plan it checked doesn't fit. A green tick on
    // "Doesn't fit a 20-minute day" read as a pass; this is a caution.
    return <Pill icon={<AlertCircle className="h-3.5 w-3.5" />} text={summary} tone="warning" />
  }
  return <Pill icon={<CheckCircle2 className="h-3.5 w-3.5" />} text={summary} tone="success" />
}

function SoftConfirmView({
  call,
  busy,
  onApply,
  onCancel,
}: {
  call: ToolCardCall
  busy: boolean
  onApply: () => void
  onCancel: () => void
}) {
  const label = softConfirmLabel(call)
  return (
    <div className="my-2 rounded-lg border border-brand/30 bg-brand/10 dark:bg-brand/20 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="text-sm text-foreground">{label}</div>
      </div>
      <div className="mt-2 flex gap-2">
        <Button
          size="sm"
          disabled={busy}
          onClick={onApply}
          className="bg-brand text-on-brand hover:bg-brand-hover"
        >
          {busy ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <>
              {confirmVerb(call.name)} <ArrowRight className="ml-1 h-3 w-3" />
            </>
          )}
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  )
}

function HardConfirmView({
  call,
  busy,
  onApply,
  onCancel,
}: {
  call: ToolCardCall
  busy: boolean
  onApply: () => void
  onCancel: () => void
}) {
  const preview = call.preview ?? call.args
  return (
    <div className="my-2 rounded-lg border border-orange-500/30 bg-orange-50 dark:bg-orange-950/20 p-3">
      <div className="text-xs uppercase tracking-wide text-orange-700 dark:text-orange-300/80">
        Confirm change — {humanize(call.name)}
      </div>
      <DiffPreview name={call.name} preview={preview} />
      <div className="mt-3 flex gap-2">
        <Button
          size="sm"
          disabled={busy}
          onClick={onApply}
          className="bg-orange-500 text-slate-950 hover:bg-orange-400"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : confirmVerb(call.name)}
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  )
}

/* ------------------- Helpers ------------------- */

function Pill({
  icon,
  text,
  tone = 'success',
}: {
  icon: React.ReactNode
  text: string
  tone?: 'success' | 'error' | 'warning' | 'muted'
}) {
  const colorClasses =
    tone === 'success'
      ? 'border-emerald-500/30 bg-emerald-50 dark:bg-emerald-950/20 text-emerald-800 dark:text-emerald-200'
      : tone === 'warning'
        ? 'border-amber-500/40 bg-amber-50 dark:bg-amber-950/20 text-amber-900 dark:text-amber-200'
        : tone === 'error'
          ? 'border-red-500/30 bg-red-50 dark:bg-red-950/20 text-red-800 dark:text-red-200'
          : 'border-border/50 bg-card/40 text-muted-foreground'
  return (
    <div
      className={`my-1 inline-flex items-center gap-2 rounded-md border px-2.5 py-1 text-xs ${colorClasses}`}
    >
      {icon}
      <span>{text}</span>
    </div>
  )
}

function humanize(name: string): string {
  return name
    .split('_')
    .map((w) => w[0]?.toUpperCase() + w.slice(1))
    .join(' ')
}

// What an auto tool says while it runs. "Running Offer Activity..." is the
// tool's internal name; the student is waiting for something to start.
function runningLabel(name: string): string {
  if (name === 'offer_activity') return 'Setting up your practice…'
  return `Running ${humanize(name)}...`
}

function softConfirmLabel(call: ToolCardCall): string {
  if (call.name === 'start_drill') {
    const args = call.args as { label?: string; startUrl?: string }
    return args.label ?? 'Start the drill'
  }
  if (call.name === 'set_focus_topic') {
    const args = call.args as { topic?: string; weight?: string }
    if (args.weight === 'remove') return `Remove "${args.topic}" from your focus list?`
    return `Add "${args.topic}" to your focus list (${args.weight} priority)?`
  }
  return `${humanize(call.name)}?`
}

// The confirm button's label. "Apply" read as vague UI jargon — a verb that
// names the actual action ("Start drill") is clearer and matches what the
// student just agreed to.
function confirmVerb(name: string): string {
  switch (name) {
    case 'start_drill':
      return 'Start drill'
    case 'generate_drill':
      return 'Create drill'
    case 'set_focus_topic':
      return 'Update focus'
    default:
      return 'Confirm'
  }
}

function summarizeResult(call: ToolCardCall, result: ToolCardResult): string {
  const r = result.result as Record<string, unknown> | undefined
  if (!r) return `${humanize(call.name)} ran`

  if (call.name === 'get_my_progress') {
    const overall = (r.overall as { weightedAccuracy?: number | null })?.weightedAccuracy
    if (overall === null || overall === undefined) return 'Pulled progress (no data yet)'
    return `Pulled progress (${Math.round((overall as number) * 100)}% ${METRIC_LABELS.recent.inline})`
  }
  if (call.name === 'recommend_focus_topic') {
    // { topics: [...] } now; a bare array on rows saved before that.
    const arr = (Array.isArray(r) ? r : ((r as { topics?: unknown }).topics ?? [])) as Array<{
      topic: string
    }>
    if (Array.isArray(arr) && arr.length > 0) {
      return `Top focus picks: ${arr
        .slice(0, 3)
        .map((t) => t.topic)
        .join(', ')}`
    }
    return 'Picked focus topics'
  }
  if (call.name === 'generate_drill') {
    // Only reached when no Start card could be built from the result.
    const qs = (r.questions as unknown as Array<unknown>) ?? []
    return `Drill ready (${qs.length} questions on ${(r.topic as string) ?? 'your topic'})`
  }
  if (call.name === 'start_drill') {
    return `Drill started`
  }
  if (call.name === 'set_focus_topic') {
    return `Focus updated (${(r.totalFocusTopics as number) ?? '?'} topics total)`
  }
  if (call.name === 'update_target_score') {
    return `Target score updated to ${(r.newTargetScore as number) ?? '?'}`
  }
  if (call.name === 'update_test_date') {
    const d = (r.newTestDate as string) ?? null
    return `Test date updated${d ? ` to ${new Date(d).toLocaleDateString()}` : ''}`
  }
  if (call.name === 'update_study_plan') {
    return 'Study plan updated'
  }
  if (call.name === 'find_similar_questions') {
    const sim = (r.similar as Array<unknown>) ?? []
    return `Found ${sim.length} similar question${sim.length === 1 ? '' : 's'}`
  }
  if (call.name === 'get_score_projection') {
    const ps = r.projectedScore as number | null
    return ps !== null && ps !== undefined
      ? `Projected score: ${ps}`
      : 'Score projection (not enough data yet)'
  }
  if (call.name === 'check_plan_time') {
    return planCheckSummary(r as Parameters<typeof planCheckSummary>[0])
  }
  return `${humanize(call.name)} ran`
}

function DiffPreview({ name, preview }: { name: string; preview: unknown }) {
  const p = preview as Record<string, unknown> | null
  if (!p) return null

  if (name === 'update_target_score') {
    return (
      <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
        <DiffRow
          label="Target"
          before={(p.previousTargetScore as number | null) ?? '—'}
          after={(p.newTargetScore as number) ?? '—'}
        />
        <DiffRow
          label="Projected"
          before={(p.projectedScore as number | null) ?? '—'}
          after={(p.projectedScore as number | null) ?? '—'}
        />
        <DiffRow
          label="Gap"
          before={String(p.previousGap ?? '—')}
          after={String(p.newGap ?? '—')}
        />
        {typeof p.reason === 'string' && <Reason reason={p.reason} />}
      </dl>
    )
  }
  if (name === 'update_test_date') {
    const fmt = (d: unknown) => (typeof d === 'string' ? new Date(d).toLocaleDateString() : '—')
    return (
      <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
        <DiffRow label="Test date" before={fmt(p.previousTestDate)} after={fmt(p.newTestDate)} />
        <DiffRow
          label="Days remaining"
          before={String(p.previousDaysFromNow ?? '—')}
          after={String(p.newDaysFromNow ?? '—')}
        />
        {typeof p.reason === 'string' && <Reason reason={p.reason} />}
      </dl>
    )
  }
  if (name === 'update_study_plan') {
    const before = p.before as Record<string, unknown>
    const after = p.after as Record<string, unknown>
    const fmt = (d: unknown) => (typeof d === 'string' ? new Date(d).toLocaleDateString() : '—')
    return (
      <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
        {after.targetScore !== before?.targetScore && (
          <DiffRow
            label="Target"
            before={(before.targetScore as number | null) ?? '—'}
            after={(after.targetScore as number | null) ?? '—'}
          />
        )}
        {after.testDate !== before?.testDate && (
          <DiffRow label="Test date" before={fmt(before.testDate)} after={fmt(after.testDate)} />
        )}
        {Array.isArray(after.focusTopicsAdded) &&
          (after.focusTopicsAdded as string[]).length > 0 && (
            <div className="col-span-2 text-xs text-muted-foreground">
              + Focus: {(after.focusTopicsAdded as string[]).join(', ')}
            </div>
          )}
        {Array.isArray(after.focusTopicsRemoved) &&
          (after.focusTopicsRemoved as string[]).length > 0 && (
            <div className="col-span-2 text-xs text-muted-foreground">
              − Focus: {(after.focusTopicsRemoved as string[]).join(', ')}
            </div>
          )}
        {typeof p.reason === 'string' && <Reason reason={p.reason} />}
      </dl>
    )
  }
  // Fallback: dump the preview as JSON (small, indented)
  return (
    <pre className="mt-2 max-h-40 overflow-y-auto rounded bg-card/60 p-2 text-[11px] text-muted-foreground">
      {JSON.stringify(p, null, 2)}
    </pre>
  )
}

function DiffRow({
  label,
  before,
  after,
}: {
  label: string
  before: React.ReactNode
  after: React.ReactNode
}) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-foreground">
        <span className="text-muted-foreground line-through">{String(before)}</span>{' '}
        <ArrowRight className="inline h-3 w-3 text-muted-foreground" />{' '}
        <span className="text-orange-800 dark:text-orange-200">{String(after)}</span>
      </dd>
    </>
  )
}

function Reason({ reason }: { reason: string }) {
  return <div className="col-span-2 mt-1 text-xs italic text-muted-foreground">{reason}</div>
}
